import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon, ClapperboardIcon, SendIcon } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
/*
  EMAIL SETUP INSTRUCTIONS
  ─────────────────────────────────────────────────────────────────
  This form uses EmailJS to send waitlist notifications to lazich.alexander@gmail.com.

  To activate email sending:
  1. Create a free account at https://www.emailjs.com
  2. Add an Email Service (Gmail recommended) — note your Service ID
  3. Create an Email Template with these variables:
       {{from_name}}   — submitter's name
       {{from_email}}  — submitter's email
       {{company}}     — submitter's company (optional)
       {{message}}     — auto-generated summary message
     Set the "To Email" in the template to: lazich.alexander@gmail.com
  4. Copy your Public Key from Account > API Keys
  5. Replace the three placeholder values below:

  const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';
  const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
  const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';

  Then uncomment the emailjs import and sendForm call below.
  ─────────────────────────────────────────────────────────────────
*/
import emailjs from '@emailjs/browser';
const EMAILJS_SERVICE_ID = 'service_2xcofq6';
const EMAILJS_TEMPLATE_ID = 'template_ne7ktb7';
const EMAILJS_PUBLIC_KEY = 'RDt5owZ127HeLt1LG';
type FormState = 'idle' | 'submitting' | 'success' | 'error';
export function WaitlistPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setFormState('submitting');
    setErrorMessage('');
    const templateParams = {
      from_name: name.trim(),
      from_email: email.trim(),
      company: company.trim() || 'Not provided',
      message: `New waitlist signup:\n\nName: ${name.trim()}\nEmail: ${email.trim()}\nCompany: ${company.trim() || 'Not provided'}`,
      to_email: 'lazich.alexander@gmail.com'
    };
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      // ── DEMO MODE ──────────────────────────────────────────────
      // Remove this block once EmailJS is configured above.
      if (EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID') {
        console.log('Waitlist signup (demo mode):', templateParams);
        await new Promise(resolve => setTimeout(resolve, 1000)); // simulate network
      }
      // ── END DEMO MODE ──────────────────────────────────────────
      setFormState('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setErrorMessage('Something went wrong. Please try again or email us directly.');
      setFormState('error');
    }
  };
  if (formState === 'success') {
    return <main className="w-full bg-cream min-h-screen flex items-center justify-center px-6 py-24" data-id="element-1208">
        <div className="max-w-lg w-full text-center" data-id="element-1209">
          <div className="w-20 h-20 bg-amber-light rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-amber/30" data-id="element-1210">
            <CheckIcon className="w-10 h-10 text-amber" aria-hidden="true" data-id="element-1211" />
          </div>
          <h1 className="font-serif text-4xl font-bold text-ink mb-4" data-id="element-1212">
            You're on the list.
          </h1>
          <p className="font-sans text-lg text-ink-muted leading-relaxed mb-8" data-id="element-1213">
            Thanks, <strong className="text-ink font-medium" data-id="element-1214">{name}</strong>.
            We'll be in touch at{' '}
            <strong className="text-ink font-medium" data-id="element-1215">{email}</strong> when your
            spot opens up.
          </p>
          <div className="bg-parchment rounded-2xl p-6 border border-parchment/80 mb-10 text-left space-y-3" data-id="element-1216">
            <p className="font-sans text-sm text-ink-muted flex items-start gap-2.5" data-id="element-1217">
              <span className="text-amber mt-0.5" data-id="element-1218">✦</span>
              Early Access members get first access to new features
            </p>
            <p className="font-sans text-sm text-ink-muted flex items-start gap-2.5" data-id="element-1219">
              <span className="text-amber mt-0.5" data-id="element-1220">✦</span>
              You'll have direct input on the product roadmap
            </p>
            <p className="font-sans text-sm text-ink-muted flex items-start gap-2.5" data-id="element-1221">
              <span className="text-amber mt-0.5" data-id="element-1222">✦</span>
              Early Access pricing locked in when we launch
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1223">
            <Link to="/features" className="font-sans text-sm font-medium text-ink-muted hover:text-ink px-6 py-3 rounded-full border border-parchment hover:border-ink-light transition-colors flex items-center justify-center gap-2" data-id="element-1224">
              Explore features
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1225" />
            </Link>
            <Link to="/" className="font-sans text-sm font-medium text-ink-muted hover:text-ink px-6 py-3 rounded-full border border-parchment hover:border-ink-light transition-colors" data-id="element-1226">
              Back to home
            </Link>
          </div>
        </div>
      </main>;
  }
  return <main className="w-full bg-cream min-h-screen" data-id="element-1227">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24" data-id="element-1228">
        <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-1229">
          {/* Left: Copy */}
          <div className="lg:sticky lg:top-32" data-id="element-1230">
            <Badge variant="amber" className="mb-6" data-id="element-1231">
              Early Access
            </Badge>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight" data-id="element-1232">
              Be first in the door.
            </h1>
            <p className="font-sans text-lg text-ink-muted leading-relaxed mb-8 max-w-md" data-id="element-1233">
              Slate & Page is in active development. Join the waitlist to get
              early access, lock in founding member pricing, and help shape the
              product.
            </p>

            <div className="space-y-5 mb-10" data-id="element-1234">
              <div className="flex items-start gap-4" data-id="element-1235">
                <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1236">
                  <span className="font-serif font-bold text-amber text-sm" data-id="element-1237">
                    1
                  </span>
                </div>
                <div data-id="element-1238">
                  <h3 className="font-serif text-base font-semibold text-ink mb-1" data-id="element-1239">
                    Early access
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-1240">
                    Get access before the public launch. Be among the first
                    writers to use the full product.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-1241">
                <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1242">
                  <span className="font-serif font-bold text-amber text-sm" data-id="element-1243">
                    2
                  </span>
                </div>
                <div data-id="element-1244">
                  <h3 className="font-serif text-base font-semibold text-ink mb-1" data-id="element-1245">
                    Founding member pricing
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-1246">
                    Early Access members lock in a discounted rate that stays
                    with them as long as they're subscribed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-1247">
                <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1248">
                  <span className="font-serif font-bold text-amber text-sm" data-id="element-1249">
                    3
                  </span>
                </div>
                <div data-id="element-1250">
                  <h3 className="font-serif text-base font-semibold text-ink mb-1" data-id="element-1251">
                    Shape the roadmap
                  </h3>
                  <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-1252">
                    Direct access to the team. Your feedback directly influences
                    what we build next.
                  </p>
                </div>
              </div>
            </div>

            {/* Mini app preview */}
            <div className="bg-navy rounded-2xl p-5 hidden lg:block" data-id="element-1253">
              <div className="flex items-center gap-2 mb-4" data-id="element-1254">
                <div className="w-6 h-6 bg-amber/20 rounded-md flex items-center justify-center" data-id="element-1255">
                  <ClapperboardIcon className="w-3.5 h-3.5 text-amber" aria-hidden="true" data-id="element-1256" />
                </div>
                <span className="font-serif text-sm font-semibold text-white" data-id="element-1257">
                  Slate & Page
                </span>
                <span className="ml-auto font-sans text-xs bg-amber/20 text-amber px-2 py-0.5 rounded-full border border-amber/20" data-id="element-1258">
                  Beta
                </span>
              </div>
              <div className="space-y-2" data-id="element-1259">
                <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-id="element-1260">
                  <div className="font-sans text-xs text-white/40 mb-1" data-id="element-1261">
                    Active project
                  </div>
                  <div className="font-sans text-sm font-medium text-white" data-id="element-1262">
                    The Long Road
                  </div>
                  <div className="flex gap-2 mt-2" data-id="element-1263">
                    <span className="font-sans text-xs bg-amber/20 text-amber px-2 py-0.5 rounded-full" data-id="element-1264">
                      12 characters
                    </span>
                    <span className="font-sans text-xs bg-white/10 text-white/60 px-2 py-0.5 rounded-full" data-id="element-1265">
                      34 scenes
                    </span>
                  </div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-id="element-1266">
                  <div className="font-sans text-xs text-white/40 mb-1" data-id="element-1267">
                    Last edited
                  </div>
                  <div className="font-sans text-sm text-white/80" data-id="element-1268">
                    Elena Marsh · Character profile
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div data-id="element-1269">
            <div className="bg-white rounded-2xl border border-parchment shadow-sm p-8 md:p-10" data-id="element-1270">
              <div className="mb-8" data-id="element-1271">
                <h2 className="font-serif text-2xl font-bold text-ink mb-2" data-id="element-1272">
                  Request early access
                </h2>
                <p className="font-sans text-sm text-ink-muted" data-id="element-1273">
                  We'll reach out when your spot is ready. No spam, ever.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-1274">
                {/* Name */}
                <div data-id="element-1275">
                  <label htmlFor="waitlist-name" className="block font-sans text-sm font-medium text-ink mb-1.5" data-id="element-1276">
                    Your name{' '}
                    <span className="text-amber" aria-hidden="true" data-id="element-1277">
                      *
                    </span>
                  </label>
                  <input id="waitlist-name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Elena Marsh" className="w-full font-sans text-sm bg-cream border border-parchment rounded-xl px-4 py-3 text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-colors" aria-required="true" data-id="element-1278" />
                </div>

                {/* Email */}
                <div data-id="element-1279">
                  <label htmlFor="waitlist-email" className="block font-sans text-sm font-medium text-ink mb-1.5" data-id="element-1280">
                    Email address{' '}
                    <span className="text-amber" aria-hidden="true" data-id="element-1281">
                      *
                    </span>
                  </label>
                  <input id="waitlist-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full font-sans text-sm bg-cream border border-parchment rounded-xl px-4 py-3 text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-colors" aria-required="true" data-id="element-1282" />
                </div>

                {/* Company */}
                <div data-id="element-1283">
                  <label htmlFor="waitlist-company" className="block font-sans text-sm font-medium text-ink mb-1.5" data-id="element-1284">
                    Company or studio
                    <span className="font-sans text-xs text-ink-light font-normal ml-2" data-id="element-1285">
                      (optional)
                    </span>
                  </label>
                  <input id="waitlist-company" type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Independent / Studio name" className="w-full font-sans text-sm bg-cream border border-parchment rounded-xl px-4 py-3 text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-colors" data-id="element-1286" />
                </div>

                {/* Error message */}
                {formState === 'error' && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3" role="alert" data-id="element-1287">
                    <p className="font-sans text-sm text-red-700" data-id="element-1288">
                      {errorMessage}
                    </p>
                  </div>}

                {/* Submit */}
                <button type="submit" disabled={formState === 'submitting' || !name.trim() || !email.trim()} className="w-full font-sans text-sm font-medium bg-amber hover:bg-amber-dark disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-md mt-2" data-id="element-1289">
                  {formState === 'submitting' ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" data-id="element-1290"></span>
                      Sending…
                    </> : <>
                      <SendIcon className="w-4 h-4" aria-hidden="true" data-id="element-1291" />
                      Join the Waitlist
                    </>}
                </button>

                <p className="font-sans text-xs text-ink-light text-center pt-1" data-id="element-1292">
                  ✦ Free forever plan &nbsp;·&nbsp; No credit card required
                  &nbsp;·&nbsp; Cancel anytime
                </p>
              </form>
            </div>

            {/* Social proof strip */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-id="element-1293">
              <span className="font-sans text-xs text-ink-light flex items-center gap-1.5" data-id="element-1294">
                <CheckIcon className="w-3.5 h-3.5 text-amber" aria-hidden="true" data-id="element-1295" />
                47 writers already on the list
              </span>
              <span className="font-sans text-xs text-ink-light flex items-center gap-1.5" data-id="element-1296">
                <CheckIcon className="w-3.5 h-3.5 text-amber" aria-hidden="true" data-id="element-1297" />
                Invites rolling out weekly
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>;
}